import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthState } from 'src/shared/store/state/auth.state';

import { IPointing, IPointingHistoryMonth, IPointings } from '../../interfaces/garderie.interface';
import { INurserySettingDTOIn, INurserySettingsDTOIn } from '../../interfaces/nursery.interface';
import { NurseryService } from '../../services/nursery.service';
import { getMonthTrad } from '../../utils/date.utils';
import {
    AddNursery,
    DeleteNursery,
    GetNurseryHistory,
    GetNurserySettings,
    GetTotalOfPointingsByMonth,
    ResetNursery,
    SearchPointing,
    UpdateNurserySettings,
} from '../actions/nursery.action';
import { ITotalOfPointingPerMonth } from './../../interfaces/nursery.interface';
import { IPagination } from './../../interfaces/pagination.interface';

export interface NurseryStateModel {
    nurseryHistory: IPointing[];
    settings: INurserySettingDTOIn[];
    isLoaded: boolean;
    currentDayPointing: IPointing[];
    pointingHistory: IPointingHistoryMonth[];
    searchPointing: IPointing[];
    pagination: IPagination;
}

@State<NurseryStateModel>({
    name: 'Nursery',
    defaults: {
        nurseryHistory: [],
        settings: [],
        isLoaded: false,
        currentDayPointing: [],
        pointingHistory: [],
        searchPointing: [],
        pagination: {
            total_items: 50,
            total_pages: 1,
            per_page: 50,
            current_page: 1,
        },
    },
})
@Injectable()
export class NurseryState {
    @Selector()
    public static hasMorePointingToShow(state: NurseryStateModel): boolean {
        return state.pagination?.current_page < state.pagination?.total_pages;
    }

    @Selector()
    public static currentPage(state: NurseryStateModel): number {
        return state.pagination.current_page;
    }

    @Selector()
    public static getNurseryHistory(state: NurseryStateModel): IPointing[] {
        return state.nurseryHistory;
    }

    @Selector()
    public static getNurserySettings(state: NurseryStateModel): INurserySettingDTOIn[] {
        return state.settings;
    }

    @Selector()
    public static getNurserySettingsPrimaire(state: NurseryStateModel): INurserySettingDTOIn {
        return state.settings.find((setting: INurserySettingDTOIn) => setting.education_level_id === 1);
    }

    @Selector()
    public static getNurserySettingsMaternel(state: NurseryStateModel): INurserySettingDTOIn {
        return state.settings.find((setting: INurserySettingDTOIn) => setting.education_level_id === 2);
    }

    @Selector()
    public static getIsNurserySettingsLoaded(state: NurseryStateModel): boolean {
        return state.isLoaded;
    }

    @Selector()
    public static currentDayPointing(state: NurseryStateModel): IPointing[] {
        return state.currentDayPointing;
    }

    @Selector()
    public static pointingHistory(state: NurseryStateModel): IPointingHistoryMonth[] {
        return state.pointingHistory;
    }

    @Selector()
    public static searchPointing(state: NurseryStateModel): IPointing[] {
        return state.searchPointing;
    }

    constructor(private nurseryService: NurseryService, private store: Store) {}

    @Action(GetNurseryHistory)
    public getNurseryHistory(ctx: StateContext<NurseryStateModel>, action: GetNurseryHistory): Observable<IPointings> {
        const state: NurseryStateModel = ctx.getState();
        const selectedStudent: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        if (action.payload.showMore && action.payload.page + 1 > state.pagination.total_pages) {
            return null;
        }
        if (!action.payload.dateFrom && !action.payload.dateTo) {
            return this.nurseryService
                .getNurseryHistory({
                    studentId: selectedStudent,
                    paymentStatus: action.payload.paymentStatus,
                    page: action.payload.showMore ? state.pagination.current_page + 1 : 1,
                })
                .pipe(
                    tap((result: IPointings) => {
                        if (result) {
                            ctx.patchState({
                                currentDayPointing: result.pointings,
                                pagination: result.paging,
                            });
                        }
                    })
                );
        } else {
            const currentMonthIndex: number = +action.payload.dateFrom.split('-')[1] - 1;
            return this.nurseryService
                .getNurseryHistory({
                    studentId: selectedStudent,
                    dateFrom: action.payload.dateFrom,
                    dateTo: action.payload.dateTo,
                    paymentStatus: action.payload.paymentStatus,
                    page: action.payload.showMore ? state.pagination.current_page + 1 : 1,
                })
                .pipe(
                    tap((result: IPointings) => {
                        if (result) {
                            ctx.setState(
                                patch({
                                    pointingHistory: updateItem<IPointingHistoryMonth>(
                                        (month: IPointingHistoryMonth) => month.index === currentMonthIndex,
                                        patch({
                                            pointings: action.payload.showMore ? append(result.pointings) : result.pointings,
                                        })
                                    ),
                                    pagination: result.paging,
                                })
                            );
                        }
                    })
                );
        }
    }

    @Action(SearchPointing)
    public searchPointing(ctx: StateContext<NurseryStateModel>, action: SearchPointing): Observable<IPointings> {
        const state: NurseryStateModel = ctx.getState();
        const selectedStudent: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        if (action.payload.showMore && action.payload.page + 1 > state.pagination.total_pages) {
            return null;
        }
        return this.nurseryService
            .getNurseryHistory({
                studentId: selectedStudent,
                dateFrom: action.payload.dateFrom,
                dateTo: action.payload.dateTo,
                search: action.payload.search,
                paymentStatus: action.payload.paymentStatus,
                period: action.payload.period,
                page: action.payload.showMore ? state.pagination.current_page + 1 : 1,
            })
            .pipe(
                tap((result: IPointings) => {
                    if (result) {
                        ctx.setState(
                            patch({
                                searchPointing: action.payload.showMore ? append(result.pointings) : result.pointings,
                                pagination: result.paging,
                            })
                        );
                    }
                })
            );
    }

    @Action(GetNurserySettings)
    public getNurserySettings(ctx: StateContext<NurseryStateModel>, action: GetNurserySettings): Observable<INurserySettingsDTOIn> {
        return this.nurseryService.getNurserySettings(action.schoolId).pipe(
            tap((result: INurserySettingsDTOIn) => {
                if (result) {
                    ctx.patchState({
                        settings: result.pointing_settings,
                        isLoaded: true,
                    });
                }
            })
        );
    }

    @Action(AddNursery)
    public addNursery(ctx: StateContext<NurseryStateModel>, action: AddNursery): Observable<IPointing> {
        return this.nurseryService.addNursery(action.nursery).pipe(
            tap((result: IPointing) => {
                if (result) {
                    ctx.setState(
                        patch({
                            nurseryHistory: append<IPointing>([result]),
                        })
                    );
                    ctx.dispatch(new GetTotalOfPointingsByMonth());
                }
            })
        );
    }

    @Action(UpdateNurserySettings)
    public updateNurserySettings(ctx: StateContext<NurseryStateModel>, action: UpdateNurserySettings): Observable<any> {
        return this.nurseryService.updateNurserySettings(action.settings, action.id).pipe(
            tap((result: INurserySettingDTOIn) => {
                if (result) {
                    ctx.setState(
                        patch({
                            settings: updateItem<INurserySettingDTOIn>((setting: INurserySettingDTOIn) => setting.id === result.id, result),
                        })
                    );
                }
            })
        );
    }

    @Action(DeleteNursery)
    public deleteNursery(ctx: StateContext<NurseryStateModel>, action: DeleteNursery): Observable<void> {
        let pointingsUpdated: IPointing[];
        return this.nurseryService.deleteNursery(action.nurseryId).pipe(
            tap(() => {
                ctx.setState(
                    patch({
                        nurseryHistory: removeItem<IPointing>((pointing: IPointing) => pointing.id === action.nurseryId),
                        searchPointing: removeItem<IPointing>((pointing: IPointing) => pointing.id === action.nurseryId),
                        pointingHistory: updateItem<IPointingHistoryMonth>(
                            (month: IPointingHistoryMonth) => {
                                const pointingToRemove: IPointing = month.pointings.find((p: IPointing) => p.id === action.nurseryId);
                                if (pointingToRemove) {
                                    pointingsUpdated = month.pointings.filter((p: IPointing) => p.id !== pointingToRemove.id);
                                }
                                return !!pointingToRemove;
                            },
                            patch({
                                pointings: pointingsUpdated,
                            })
                        ),
                    })
                );
                ctx.dispatch([new GetTotalOfPointingsByMonth()]);
            })
        );
    }

    @Action(ResetNursery)
    public resetNursery(ctx: StateContext<NurseryStateModel>): void {
        ctx.patchState({
            nurseryHistory: [],
            currentDayPointing: [],
            searchPointing: [],
            pointingHistory: [],
        });
    }

    @Action(GetTotalOfPointingsByMonth)
    public getTotalOfPointingsByMonth(ctx: StateContext<NurseryStateModel>): Observable<ITotalOfPointingPerMonth[]> {
        const selectedStudentId: number = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        return this.nurseryService.getTotalOfPointingByMonth(selectedStudentId).pipe(
            tap((result: ITotalOfPointingPerMonth[]) => {
                if (result) {
                    ctx.patchState({
                        pointingHistory: result.map((month: ITotalOfPointingPerMonth) => ({
                            index: month.month - 1,
                            name: getMonthTrad(month.month - 1),
                            pointings: [],
                            counts: {
                                total: month.pointings_count,
                                primary: month.primary_pointings_count,
                                kindergarten: month.kindergarten_pointings_count,
                            },
                        })),
                    });
                }
            })
        );
    }
}
